import { MUTIARA_JENIS, type MutiaraJenis } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Bookmark, BookmarkCheck, Send, SlidersHorizontal } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj } from '../../components/Obj.tsx'
import { Sheet } from '../../components/Sheet.tsx'
import { shareText } from '../../lib/share.ts'
import { useToast } from '../../lib/toast.ts'
import { MutiaraCardBody } from './MutiaraCardBody.tsx'
import { DECK_KEY, JENIS_INFO, jenisTint, postRead, postSaved, shareTextOf, useDeck, type Deck, type MutiaraCard } from './mutiara.ts'

/** US-10.2: a card counts as read after ≥ 3 s with more than 55% of it on screen. */
export const READ_DWELL_MS = 3000
const READ_RATIO = 0.55
const JENIS_KEY = 'iqp.mutiara.jenis'

const loadJenis = (): MutiaraJenis | undefined => {
  try {
    const v = localStorage.getItem(JENIS_KEY)
    return (MUTIARA_JENIS as readonly string[]).includes(v ?? '') ? (v as MutiaraJenis) : undefined
  } catch {
    return undefined
  }
}
const storeJenis = (j: MutiaraJenis | undefined) => {
  try {
    if (j) localStorage.setItem(JENIS_KEY, j)
    else localStorage.removeItem(JENIS_KEY)
  } catch {
    // private mode: the filter just is not remembered
  }
}

/** Calls `onRead(id)` once per card that stayed on screen long enough. */
function useReadTracker(onRead: (id: string) => void) {
  const cb = useRef(onRead)
  cb.current = onRead
  const io = useRef<IntersectionObserver | null>(null)
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const done = useRef(new Set<string>())

  // created on the first card ref (refs run before effects), torn down on unmount
  const observer = () => {
    if (io.current || typeof IntersectionObserver === 'undefined') return io.current
    const t = timers.current
    io.current = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).dataset.card!
          // "55% visible" of the card, or of the screen when the card is taller than the screen
          const need = READ_RATIO * Math.min(e.boundingClientRect.height, e.rootBounds?.height ?? window.innerHeight)
          if (e.isIntersecting && e.intersectionRect.height >= need) {
            if (done.current.has(id) || t.has(id)) continue
            t.set(id, setTimeout(() => { t.delete(id); done.current.add(id); io.current?.unobserve(e.target); cb.current(id) }, READ_DWELL_MS))
          } else if (t.has(id)) {
            clearTimeout(t.get(id))
            t.delete(id)
          }
        }
      },
      { threshold: [0, 0.25, 0.4, READ_RATIO, 0.7, 0.85, 1] },
    )
    return io.current
  }
  useEffect(() => {
    const t = timers.current
    return () => {
      io.current?.disconnect()
      io.current = null
      for (const x of t.values()) clearTimeout(x)
      t.clear()
    }
  }, [])

  return (el: HTMLElement | null) => {
    if (el && !done.current.has(el.dataset.card!)) observer()?.observe(el)
  }
}

function Drops({ n, limit, bonus }: { n: number; limit: number; bonus: boolean }) {
  const filled = Math.min(n, limit)
  return (
    <span className="mt-drops" role="img" aria-label={bonus ? `Bonus: ${n} kartu dibaca hari ini` : `${filled} dari ${limit} tetes embun hari ini`}>
      {bonus ? <span className="mt-bonus">Bonus</span> : Array.from({ length: limit }, (_, i) => <i key={i} className={i < filled ? 'on' : ''} />)}
    </span>
  )
}

function CardView({ c, track, onSave, onShare }: { c: MutiaraCard; track: (el: HTMLElement | null) => void; onSave: (c: MutiaraCard) => void; onShare: (c: MutiaraCard) => void }) {
  const info = JENIS_INFO[c.jenis]
  return (
    // already read today: nothing to count
    <article className="mt-card" data-card={c.id} ref={c.read ? undefined : track} aria-label={info.label}>
      <div className="mt-in" style={{ background: jenisTint(c.jenis) }}>
        <header className="mt-head">
          <Obj id={info.obj} size={40} />
          <span className="eyebrow">{info.label}</span>
          {c.read && <span className="pill mt-readpill">Sudah dibaca</span>}
        </header>
        <div className="mt-body">
          <MutiaraCardBody c={c} />
        </div>
        <footer className="mt-actions">
          <button type="button" className="btn btn-ghost btn-sm" aria-pressed={c.saved} onClick={() => onSave(c)}>
            {c.saved ? <BookmarkCheck size={18} aria-hidden="true" /> : <Bookmark size={18} aria-hidden="true" />} {c.saved ? 'Tersimpan' : 'Simpan'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onShare(c)}>
            <Send size={18} aria-hidden="true" /> Kirim
          </button>
        </footer>
      </div>
    </article>
  )
}

/** /mutiara: bounded feed, five cards a day, then "Cukup" (docs/PRD.md US-10.x, UX-PLAYBOOK §7.5). */
export function MutiaraPage() {
  const [jenis, setJenisState] = useState<MutiaraJenis | undefined>(loadJenis)
  const setJenis = (j: MutiaraJenis | undefined) => {
    storeJenis(j)
    setJenisState(j)
    setMore(false)
  }
  const { data, isPending, isError, refetch } = useDeck(jenis)
  const qc = useQueryClient()
  const [more, setMore] = useState(false)
  const [filter, setFilter] = useState(false)
  const [toast, show] = useToast()
  const enough = useRef<HTMLDivElement>(null)

  const patch = (fn: (d: Deck) => Deck) => {
    for (const j of [undefined, ...MUTIARA_JENIS]) qc.setQueryData<Deck>(DECK_KEY(j), (d) => (d ? fn(d) : d))
  }
  const track = useReadTracker(async (id) => {
    try {
      const r = await postRead(id)
      patch((d) => ({ ...d, readToday: r.readToday, full: r.full, cards: d.cards.map((x) => (x.id === id ? { ...x, read: true } : x)) }))
      if (r.lit) show('Lima tetes embun hari ini sudah terkumpul.')
    } catch {
      // not counted this time; the card can count on a later visit
    }
  })

  // Came back after five today: start at the "Cukup" card, not at the top.
  const startFull = useRef<boolean | null>(null)
  useEffect(() => {
    if (!data || startFull.current !== null) return
    startFull.current = data.full
    if (data.full) enough.current?.scrollIntoView?.({ block: 'start' })
  }, [data])

  const onSave = async (c: MutiaraCard) => {
    const on = !c.saved
    patch((d) => ({ ...d, cards: d.cards.map((x) => (x.id === c.id ? { ...x, saved: on } : x)) }))
    try {
      await postSaved(c.id, on)
      void qc.invalidateQueries({ queryKey: ['mutiara', 'kotak'] })
      show(on ? 'Tersimpan di Kotak Mutiara.' : 'Dikeluarkan dari Kotak Mutiara.')
    } catch {
      patch((d) => ({ ...d, cards: d.cards.map((x) => (x.id === c.id ? { ...x, saved: !on } : x)) }))
      show('Belum bisa disimpan. Cek koneksi, lalu coba lagi.')
    }
  }
  const onShare = async (c: MutiaraCard) => {
    const t = shareTextOf(c)
    const r = await shareText(t.title, t.text)
    if (r === 'shared') show('Terkirim. Semoga bermanfaat untuknya.')
    if (r === 'whatsapp') show('WhatsApp dibuka di tab baru.')
  }

  const limit = data?.limit ?? 5
  const first = data?.cards.slice(0, limit) ?? []
  const rest = data?.cards.slice(limit) ?? []
  const bonus = more || (data?.readToday ?? 0) > limit

  return (
    <div className="mt-page">
      <div className="mt-top">
        <div>
          <h1>Mutiara</h1>
          <Drops n={data?.readToday ?? 0} limit={limit} bonus={bonus} />
        </div>
        <button type="button" className="icon-btn plain" onClick={() => setFilter(true)} aria-label={`Pilih jenis kartu (${jenis ? JENIS_INFO[jenis].label : 'semua'})`}>
          <SlidersHorizontal size={20} aria-hidden="true" />
        </button>
        <Link className="icon-btn plain" to="/mutiara/kotak" aria-label="Kotak Mutiara (kartu tersimpan)">
          <Bookmark size={20} aria-hidden="true" />
        </Link>
      </div>

      {isPending && <div className="mt-feed" data-lenis-prevent aria-busy="true" aria-label="Memuat kartu"><div className="mt-card"><div className="mt-in skel sk" /></div></div>}
      {isError && (
        <div className="page">
          <EmptyState obj="jam-pasir" title="Belum bisa dimuat" action={<button className="btn btn-secondary" onClick={() => refetch()}>Coba lagi</button>}>
            Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
          </EmptyState>
        </div>
      )}
      {data && data.cards.length === 0 && (
        <div className="page">
          <EmptyState obj="embun" title="Kartu sedang disiapkan" action={jenis ? <button className="btn btn-ghost" onClick={() => setJenis(undefined)}>Tampilkan semua jenis</button> : undefined}>
            {jenis ? 'Belum ada kartu untuk jenis ini. Coba lihat semua jenis.' : 'Kartu Mutiara belum tersedia. Coba lagi nanti.'}
          </EmptyState>
        </div>
      )}
      {data && data.cards.length > 0 && (
        <div className="mt-feed" data-lenis-prevent>
          {first.map((c) => <CardView key={c.id} c={c} track={track} onSave={onSave} onShare={onShare} />)}
          <section className="mt-card mt-enough" ref={enough} aria-labelledby="mt-enough-h">
            <div className="mt-in">
              <Obj id="embun" size={120} className="empty-obj" />
              {first.length < limit ? (
                <>
                  <h2 id="mt-enough-h">Kartu hari ini sudah habis</h2>
                  <p>Besok ada kartu baru. Yang menumbuhkan adalah apa yang kamu lakukan sesudah membaca.</p>
                </>
              ) : (
                <>
                  <h2 id="mt-enough-h">Cukup untuk hari ini</h2>
                  <p>Embun hari ini sudah terkumpul lima tetes. Yang menumbuhkan adalah apa yang kamu lakukan sesudah membaca.</p>
                </>
              )}
              <Link className="btn btn-primary btn-block" to="/">Kembali ke Beranda</Link>
              {rest.length > 0 && !more && (
                <>
                  <button type="button" className="btn btn-ghost btn-block" onClick={() => setMore(true)}>Lanjut sebentar</button>
                  <p className="help">Silakan lanjut jika memang ingin.</p>
                </>
              )}
            </div>
          </section>
          {more && rest.map((c) => <CardView key={c.id} c={c} track={track} onSave={onSave} onShare={onShare} />)}
        </div>
      )}

      {filter && (
        <Sheet title="Jenis kartu" onClose={() => setFilter(false)}>
          <div className="mt-filter" role="group" aria-label="Jenis kartu">
            {[undefined, ...MUTIARA_JENIS].map((j) => (
              <button key={j ?? 'semua'} type="button" className="chip-btn" aria-pressed={jenis === j} onClick={() => { setJenis(j); setFilter(false) }}>
                {j ? JENIS_INFO[j].label : 'Semua'}
              </button>
            ))}
          </div>
          <p className="help">Pilihanmu diingat di perangkat ini. Tetes embun tetap dihitung untuk semua jenis.</p>
        </Sheet>
      )}

      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
    </div>
  )
}
